import classNames from 'classnames';
import { MaterialIcon } from 'shared/ui/MaterialIcon';
import { getKeys } from 'shared/lib/object';
import { Tooltip } from 'shared/ui/Tooltip';
import {
  HistoryAssignedEventGeneralInfoKey,
  HistoryAssignedEventStepsInfoKey,
  type LeadOrAppointmentHistoryBody,
} from 'entities/history';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';

const transformAssignedEventData = (leadOrApptBody: LeadOrAppointmentHistoryBody) => {
  const generalInfo = getKeys(HistoryAssignedEventGeneralInfoKey)
    .filter((rowKey) => rowKey in leadOrApptBody && leadOrApptBody[rowKey] !== null)
    .map((leadAssignedKey) => {
      let valueToDisplay;

      switch (leadAssignedKey) {
        case 'leadCreatedAt':
        case 'leadAssignedAt':
          valueToDisplay = leadOrApptBody[leadAssignedKey].split(' ').join(' | ');
          break;

        default:
          valueToDisplay = leadOrApptBody[leadAssignedKey];
          break;
      }

      return {
        key: HistoryAssignedEventGeneralInfoKey[leadAssignedKey],
        value: valueToDisplay,
      };
    });

  const stepsInfo = getKeys(HistoryAssignedEventStepsInfoKey)
    .filter((stepKey) => stepKey in leadOrApptBody && !!leadOrApptBody[stepKey]?.length)
    .map((existingStepKey) => {
      return {
        key: HistoryAssignedEventStepsInfoKey[existingStepKey],
        value: leadOrApptBody[existingStepKey],
      };
    });

  return { generalInfo, stepsInfo };
};

const getStepTooltipContent = (key: HistoryAssignedEventStepsInfoKey) => {
  const stepsConfig = {
    [HistoryAssignedEventStepsInfoKey.stepZeroCampaigns]: "Includes all campaigns that match the lead's zip code.",
    [HistoryAssignedEventStepsInfoKey.stepOneCampaigns]:
      'Includes campaigns that are either Running or Overbudget (Running campaigns have higher priority. Overbudget campaigns are also prioritized: lower overbudget = higher priority in the distribution).',
    [HistoryAssignedEventStepsInfoKey.stepTwoCampaigns]:
      'Includes the campaigns with the schedule that was active when a lead was distributed.',
    [HistoryAssignedEventStepsInfoKey.stepThreeCampaigns]: 'Filter three campaigns with the highest bid.',
  };

  return stepsConfig[key];
};

const rowClasses = 'inline-block w-[100%] text-xs py-2 border-t border-slate-100';

type AssignedEventProps = {
  data: LeadOrAppointmentHistoryBody;
};

export function AssignedEvent({ data }: AssignedEventProps) {
  const transformedData = transformAssignedEventData(data);

  return (
    <div data-testid="TestId__assigned-event">
      {transformedData.generalInfo.map((dataPair) => (
        <div
          className={classNames(rowClasses, 'first:border-0')}
          key={dataPair.key}
          {...getDataAutoTestAttributes([dataPair.key.toLowerCase().replaceAll(' ', '-')]).attributes}
        >
          <span className="mr-[6px] font-semibold">{dataPair.key}:</span>
          {dataPair.value}
        </div>
      ))}
      <div className={rowClasses}>
        <span
          className="mr-[6px] font-semibold"
          {...getDataAutoTestAttributes(['all-steps-header']).attributes}
        >
          Steps:
        </span>
        How leads are assigned to a campaign
      </div>
      {transformedData.stepsInfo.map((dataPair) => (
        <div key={dataPair.key}>
          <div
            className={`${rowClasses} cursor-pointer`}
            {...getDataAutoTestAttributes([`step-${dataPair.key.toLowerCase().replaceAll(' ', '-')}-header`])
              .attributes}
          >
            <ul className="flex list-inside list-disc items-center gap-2 font-semibold">
              <li>{dataPair.key}:</li>
              <Tooltip
                placement="top-start"
                content={getStepTooltipContent(dataPair.key)}
                contentClass="!text-left"
                dataTestId={`Assigned-event-${dataPair.key}`}
              >
                <MaterialIcon className="text-sm text-slate-600 hover:text-deep-blue-500">help_outline</MaterialIcon>
              </Tooltip>
            </ul>
          </div>

          <div className={classNames(rowClasses, 'py-[11px]')}>
            <ol className="list-inside list-decimal">
              {dataPair.value?.map((campaign) => (
                <li
                  key={campaign.id}
                  {...getDataAutoTestAttributes([dataPair.key.toLowerCase().replaceAll(' ', '-')]).attributes}
                >
                  {campaign.name}
                </li>
              ))}
            </ol>
          </div>
        </div>
      ))}
    </div>
  );
}
